<div class="analytics-page">
  <header class="page-header">
    <h1>Analytics Dashboard</h1>
    <p>Track idea submission trends and impact metrics</p>
  </header>

  <div *ngIf="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>

  <div *ngIf="loading" class="loading-state">
    <app-loader label="Loading analytics"></app-loader>
  </div>

  <ng-container *ngIf="!loading && dashboard as data">
    <div class="stats-grid">
      <article class="stat-card">
        <div class="stat-icon stat-icon-blue">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M4 19h16v2H2V3h2v16zm4-8 3 3 5-7 2 1.5-6.5 8.5L6.5 12.5 8 11z" />
          </svg>
        </div>
        <div class="stat-value">{{ data.totalIdeas }}</div>
        <div class="stat-label">Total Ideas</div>
      </article>

      <article class="stat-card">
        <div class="stat-icon stat-icon-purple">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
              d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.94 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
            />
          </svg>
        </div>
        <div class="stat-value">{{ data.activeContributors }}</div>
        <div class="stat-label">Active Contributors</div>
      </article>

      <article class="stat-card">
        <div class="stat-icon stat-icon-green">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
              d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-1 15-4-4 1.41-1.41L11 14.17l5.59-5.58L18 10z"
            />
          </svg>
        </div>
        <div class="stat-value">{{ formatPercentage(data.acceptanceRate) }}</div>
        <div class="stat-label">Acceptance Rate</div>
      </article>

      <article class="stat-card">
        <div class="stat-icon stat-icon-green">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
              d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-1 15-4-4 1.41-1.41L11 14.17l5.59-5.58L18 10z"
            />
          </svg>
        </div>
        <div class="stat-value">{{ formatPercentage(data.deliveryRate) }}</div>
        <div class="stat-label">Delivery Rate</div>
      </article>

      <article class="stat-card">
        <div class="stat-icon stat-icon-cyan">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
              d="M12 1a11 11 0 1 0 11 11A11.01 11.01 0 0 0 12 1zm1 11.41V6h-2v7h6v-2z"
            />
          </svg>
        </div>
        <div class="stat-value">{{ formatAverageFirstResponse(data.averageFirstResponseDays) }}</div>
        <div class="stat-label">Avg First Response</div>
      </article>
    </div>

    <div class="charts-grid">
      <section class="panel chart-panel">
        <div class="panel-header">
          <h2>Ideas by Category</h2>
        </div>

        <div *ngIf="data.categoryBreakdown.length; else emptyCategories" class="category-chart">
          <div class="category-chart-shell">
            <div class="category-y-axis">
              <div *ngFor="let tick of categoryAxisTicks" class="category-y-tick">
                {{ tick }}
              </div>
            </div>

            <div class="category-plot-area">
              <div class="category-chart-grid"></div>
              <div class="category-bars">
                <div *ngFor="let item of data.categoryBreakdown" class="category-bar-group" [attr.title]="item.totalIdeas + ' ideas'">
                  <div class="category-bar-track">
                    <div class="category-bar-fill" [style.height]="getCategoryBarHeight(item)">
                      <span class="chart-hover-tooltip" aria-hidden="true">{{ item.totalIdeas }}</span>
                    </div>
                  </div>
                  <div class="category-bar-label" [title]="item.name">{{ item.name }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="panel chart-panel">
        <div class="panel-header">
          <h2>Status Distribution</h2>
        </div>

        <div *ngIf="data.statusBreakdown.length; else emptyStatuses" class="status-chart-layout">
          <div class="status-donut-shell">
            <svg
              class="status-donut"
              viewBox="0 0 160 160"
              role="img"
              aria-label="Status distribution donut chart"
              (mouseleave)="clearActiveStatus()"
            >
              <circle
                class="status-donut-track"
                cx="80"
                cy="80"
                [attr.r]="statusChartRadius"
                fill="none"
                [attr.stroke-width]="statusChartStrokeWidth"
              ></circle>
              <circle
                *ngFor="let segment of getStatusSegments(data.statusBreakdown); trackBy: trackByStatusSegment"
                class="status-donut-segment"
                cx="80"
                cy="80"
                [attr.r]="statusChartRadius"
                fill="none"
                [attr.stroke]="getStatusColor(segment.item.key)"
                [attr.stroke-width]="statusChartStrokeWidth"
                [attr.stroke-dasharray]="segment.dasharray"
                [attr.stroke-dashoffset]="segment.dashoffset"
                [class.status-donut-segment-active]="activeStatusKey === segment.item.key"
                [attr.title]="segment.item.label + ': ' + segment.item.totalIdeas + ' ideas'"
                (mouseenter)="setActiveStatus(segment.item.key)"
              ></circle>
            </svg>
            <div class="status-donut-center">
              <ng-container *ngIf="getActiveStatusItem(data.statusBreakdown) as active; else donutTotal">
                <span class="status-donut-total">{{ active.totalIdeas }}</span>
                <span class="status-donut-caption">{{ active.label }}</span>
              </ng-container>
              <ng-template #donutTotal>
                <span class="status-donut-total">{{ data.totalIdeas }}</span>
                <span class="status-donut-caption">Ideas</span>
              </ng-template>
            </div>
            <div
              *ngFor="let item of data.statusBreakdown; trackBy: trackByStatus; let index = index"
              class="status-ring-label"
              [style.color]="getStatusColor(item.key)"
              [ngStyle]="getStatusLabelStyle(index, data.statusBreakdown)"
            >
              {{ item.label }}
            </div>
          </div>
        </div>
      </section>
    </div>

    <section class="panel table-panel">
      <div class="panel-header">
        <h2>Department Breakdown</h2>
      </div>

      <div *ngIf="data.departmentBreakdown.length; else emptyDepartments" class="table-wrapper">
        <table class="analytics-table">
          <thead>
            <tr>
              <th>Department</th>
              <th>Total Ideas</th>
              <th>Delivered</th>
              <th>Delivery Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of data.departmentBreakdown">
              <td>{{ item.departmentName }}</td>
              <td>{{ item.totalIdeas }}</td>
              <td>{{ item.deliveredIdeas }}</td>
              <td>{{ formatPercentage(item.deliveryRate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </ng-container>

  <ng-template #emptyCategories>
    <div class="empty-state">No category data available yet.</div>
  </ng-template>

  <ng-template #emptyStatuses>
    <div class="empty-state">No status data available yet.</div>
  </ng-template>

  <ng-template #emptyDepartments>
    <div class="empty-state">No department data available yet.</div>
  </ng-template>
</div>
